import React, { useCallback, useEffect, useMemo, useState } from "react";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import { useShallow } from "zustand/react/shallow";
import { TrashModel } from "target_wasm";
import { Collapsible } from "components/common/Collapsible";
import { OverlayScroll } from "components/common/OverlayScroll";
import { SearchBar } from "@/components/common/SearchBar";
import { SectionRule } from "components/common/SectionRule";
import { ImpactChips } from "components/common/ImpactRow";
import { Cluster } from "components/common/Stack";
import { Tooltip } from "@/components/common/Tooltip";
import { TooltipButton } from "components/common/TooltipButton";
import { ModelRow } from "@/components/main/overview/ModelRow";
import { ListEmptyShell } from "@/components/main/overview/ListEmptyShell";
import {
  DatasetMetaSection,
  MetaSectionAccordion,
} from "@/components/main/datasets/DatasetMetaSection";
import { NumberCount } from "@/components/common/NumberCount";
import { SpaceDot } from "@/components/main/spaces/SpaceDot";
import { ConfirmDialog } from "@/components/main/utils/ConfirmDialog";
import {
  trashModelToModelElement,
  trashRiskColor,
} from "@/components/main/utils/models/types";
import { DeleteModelDialog } from "@/components/main/utils/models/deletion/DeleteModelDialog";
import { restoreSpace, type TrashedSpace } from "@/lib/api/organizations";
import { AssetIcon } from "@/components/common/AssetIcon";
import { deletionCountdown, formatShortDayMonth } from "@/lib/dates";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import {
  spaceColor,
  useOrgContextStore,
} from "@/components/main/stores/orgContextStore";
import { useTrashStore } from "@/components/main/stores/trashStore";
import { showNotificationGlobal } from "@/stores/useNotificationStore";
import { ColumnTopSpacer, ListCount } from "@/components/main/pages/pageColumn";
import { plural } from "components/common/setUtils";

/// D53: one deleted subtree, one group. `header` is the doomed root; the
/// models are every trashed model under it, sub-spaces included.
interface TrashGroup {
  header: TrashedSpace;
  models: TrashModel[];
}

/// The sub-space of `root` a model sits in; none when it sits in the root.
const subSpaceOf = (m: TrashModel, root: TrashedSpace) =>
  root.sub_spaces.find((s) => s.space_id === m.via_space_id_js);

/// Where something in a deleted space sits, in a review's fine print: its
/// spaces, then its model, each after a small mark of its own.
const OriginPath: React.FC<{
  spaces: ({ space_id: string; name: string; color?: string } | undefined)[];
  model?: TrashModel;
}> = ({ spaces, model }) => {
  const spaceById = useOrgContextStore((s) => s.spaceById);
  const parts = spaces
    .filter((s) => s !== undefined)
    .map((s) => (
      <>
        <SpaceDot
          size="sm"
          color={spaceColor(
            spaceById(s.space_id) ?? {
              space_id: s.space_id,
              color: s.color ?? "",
            },
          )}
        />
        {s.name}
      </>
    ));
  if (model)
    parts.push(
      <>
        <AssetIcon
          size="sm"
          color={model.color_js}
          iconIndex={model.icon_js}
          className="w-3! h-3!"
        />
        {model.model_name_js || "Unnamed Model"}
      </>,
    );
  return (
    <span className="inline-flex items-center gap-1.5 align-middle">
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span aria-hidden>/</span>}
          <span className="inline-flex items-center gap-1">{part}</span>
        </React.Fragment>
      ))}
    </span>
  );
};

/// Main-app Trash page. Mirrors the Overview / Datasets shell: a
/// centered column with a fixed filter bar and a scrolling list whose section
/// header sticks. Rows reuse the Overview `ModelRow` in trash mode — same icon,
/// name, spacing, and tokens — with Restore / Delete-forever inline. No add
/// affordance, and a trashed model can't be entered. A deleted space is one
/// group with one Restore, and everything under it comes back together (D53).
export const TrashPage: React.FC = () => {
  const {
    trashModels,
    trashedSpaces,
    lastFetch,
    startPolling,
    fetchTrashModels,
    removeTrashedSpaces,
    refreshTrashModels,
  } = useTrashStore(
    useShallow((s) => ({
      trashModels: s.trashModels,
      trashedSpaces: s.trashedSpaces,
      lastFetch: s.lastTrashModelsFetch,
      startPolling: s.startPolling,
      fetchTrashModels: s.fetchTrashModels,
      removeTrashedSpaces: s.removeTrashedSpaces,
      refreshTrashModels: s.refreshTrashModels,
    })),
  );
  const { fetchModelsList, restoreModel, purgeModel } = useMainStateStore(
    useShallow((s) => ({
      fetchModelsList: s.fetchModelsList,
      restoreModel: s.restoreModel,
      purgeModel: s.purgeModel,
    })),
  );
  const refreshSpaces = useOrgContextStore((s) => s.refreshSpaces);
  // S6c/D41: the Trash is org-scoped, so a context switch is a new list.
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  const [filter, setFilter] = useState("");
  const [confirmTarget, setConfirmTarget] = useState<TrashModel | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [restoringSpace, setRestoringSpace] = useState<string | null>(null);
  // One space open at a time.
  const [openSpace, setOpenSpace] = useState<string | null>(null);

  useEffect(() => startPolling(), [startPolling]);
  useEffect(() => {
    void fetchTrashModels();
  }, [fetchTrashModels, activeOrgId]);

  const q = filter.trim().toLowerCase();
  const { groups, loose } = useMemo(() => {
    // A model's stamp names the doomed space it sits in; the group is that
    // space's ROOT, which is the only one with a header.
    const rootOf = new Map<string, TrashedSpace>();
    for (const root of trashedSpaces) {
      rootOf.set(root.space_id, root);
      for (const sub of root.sub_spaces) rootOf.set(sub.space_id, root);
    }
    const byRoot = new Map<string, TrashGroup>(
      trashedSpaces.map((h) => [h.space_id, { header: h, models: [] }]),
    );
    const loose: TrashModel[] = [];
    for (const m of trashModels) {
      if (
        q &&
        !(m.model_name_js || "Unnamed Model").toLowerCase().includes(q)
      ) {
        continue;
      }
      const root = m.via_space_id_js
        ? rootOf.get(m.via_space_id_js)
        : undefined;
      if (!root) {
        loose.push(m);
        continue;
      }
      byRoot.get(root.space_id)?.models.push(m);
    }
    const groups = [...byRoot.values()].filter(
      (g) =>
        !q ||
        g.models.length > 0 ||
        g.header.name.toLowerCase().includes(q) ||
        g.header.sub_spaces.some((s) => s.name.toLowerCase().includes(q)),
    );
    return { groups, loose };
  }, [trashModels, trashedSpaces, q]);

  const restore = useCallback(
    async (model: TrashModel) => {
      const outcome = await restoreModel(model.model_id_js);
      showNotificationGlobal(
        outcome.ok ? "Model restored from trash" : outcome.message,
        outcome.ok ? "success" : "error",
      );
    },
    [restoreModel],
  );

  const restoreWholeSpace = useCallback(
    async (group: TrashGroup) => {
      setRestoringSpace(group.header.space_id);
      try {
        const { spaces_restored, models_restored } = await restoreSpace(
          group.header.space_id,
        );
        const subSpaces = Math.max(spaces_restored.length - 1, 0);
        const parts = [
          subSpaces > 0 && plural(subSpaces, "sub-space"),
          models_restored > 0 && plural(models_restored, "model"),
        ].filter(Boolean);
        showNotificationGlobal(
          parts.length > 0
            ? `${group.header.name} restored with ${parts.join(" and ")}`
            : `${group.header.name} restored`,
          "success",
        );
        removeTrashedSpaces(spaces_restored);
        void fetchModelsList({ refresh: true });
        void refreshSpaces();
      } catch (e) {
        showNotificationGlobal(
          e instanceof Error ? e.message : "Could not restore the space",
          "error",
        );
      } finally {
        setRestoringSpace(null);
      }
    },
    [removeTrashedSpaces, fetchModelsList, refreshSpaces],
  );

  const confirmDelete = useCallback(async () => {
    if (!confirmTarget) return;
    setDeleting(true);
    const ok = await purgeModel(confirmTarget.model_id_js);
    setDeleting(false);
    setConfirmTarget(null);
    showNotificationGlobal(
      ok ? "Model permanently deleted" : "Failed to permanently delete model",
      ok ? "success" : "error",
    );
  }, [confirmTarget, purgeModel]);

  const row = (m: TrashModel, group: TrashGroup | null) => {
    const element = trashModelToModelElement(m);
    if (!element) return null;
    const countdown = deletionCountdown(m.scheduled_deletion_date_js);
    return (
      <ModelRow
        key={m.model_id_js}
        model={element}
        trash={{
          countdownLabel: countdown.label,
          countdownTooltip: countdown.tooltip,
          onRestore: () => void restore(m),
          onDeleteForever: () => setConfirmTarget(m),
          trashModel: m,
          // A transfer changes which tables are still scheduled, so re-read
          // the list on close.
          onAnalysisClosed: () => void refreshTrashModels(),
          // A model in a doomed space comes back only with the space (D53).
          // Without a group the caller does not manage that space, so the
          // restore is someone else's — the door would 409 anyway.
          restoreLocked: group
            ? `Comes back with ${group.header.name} — restore the space`
            : m.via_space_name_js
              ? `Comes back with ${m.via_space_name_js} — a manager of that space restores it`
              : undefined,
          hideRestore: group !== null,
          location: group ? (
            <OriginPath spaces={[group.header, subSpaceOf(m, group.header)]} />
          ) : (
            m.via_space_id_js && (
              <OriginPath
                spaces={[
                  {
                    space_id: m.via_space_id_js,
                    name: m.via_space_name_js ?? "",
                  },
                ]}
              />
            )
          ),
        }}
      />
    );
  };

  const isEmpty = trashModels.length === 0 && trashedSpaces.length === 0;
  const nothingMatches = loose.length === 0 && groups.length === 0;

  return (
    <div className="relative flex flex-col items-center w-full h-full overflow-y-auto px-6">
      <ColumnTopSpacer />
      {/* The column holds the window's height and the list scrolls inside it. */}
      <div
        className="w-full flex flex-col flex-1 min-h-0"
        style={{
          maxWidth: "45rem",
          paddingBottom: "2.5rem",
        }}
      >
        {isEmpty ? (
          <ListEmptyShell
            icon={<DeleteOutlineOutlinedIcon sx={{ fontSize: 14 }} />}
            label="Trash"
            hint="Deleted models and spaces rest here for 30 days before they're permanently removed."
            // Until the first fetch resolves; the store keeps the list warm
            // across navigations, so re-opening Trash never re-shows it.
            loading={lastFetch === null}
            search={{
              value: filter,
              onChange: setFilter,
              placeholder: "Search trash",
            }}
          />
        ) : (
          <>
            <div className="shrink-0 mb-6">
              <SearchBar
                value={filter}
                onChange={setFilter}
                placeholder="Search trash"
                size="lg"
              />
            </div>

            {/* Above the scroll box, so an open space's header sticks to its top. */}
            <div className="shrink-0">
              <SectionRule
                inset
                icon={<DeleteOutlineOutlinedIcon sx={{ fontSize: 14 }} />}
                label="Trash"
              />
            </div>
            <OverlayScroll
              frameClassName="flex flex-col flex-[0_1_auto] min-h-0"
              className="overflow-x-hidden"
            >
              {nothingMatches ? (
                <div className="text-center py-12 text-small text-fg-muted italic">
                  Nothing matches “{filter.trim()}”.
                </div>
              ) : (
                <>
                  {loose.map((m) => row(m, null))}
                  {groups.map((g) => (
                    <TrashSpace
                      key={g.header.space_id}
                      group={g}
                      // A search opens every space holding a match.
                      open={
                        openSpace === g.header.space_id ||
                        (q !== "" && g.models.length > 0)
                      }
                      onToggle={() =>
                        setOpenSpace((was) =>
                          was === g.header.space_id ? null : g.header.space_id,
                        )
                      }
                      searching={q !== ""}
                      restoring={restoringSpace === g.header.space_id}
                      onRestore={() => void restoreWholeSpace(g)}
                      onReviewClosed={() => void refreshTrashModels()}
                    >
                      {g.models.map((m) => row(m, g))}
                    </TrashSpace>
                  ))}
                </>
              )}
            </OverlayScroll>

            {/* Pinned below the scroll so the count stays visible. */}
            <div className="shrink-0 flex items-center mt-6 px-1">
              <ListCount
                parts={[
                  trashModels.length > 0 && plural(trashModels.length, "model"),
                  trashedSpaces.length > 0 &&
                    plural(trashedSpaces.length, "space"),
                ]}
              />
            </div>
          </>
        )}
      </div>
      <ConfirmDialog
        open={confirmTarget !== null}
        title="Permanently delete model"
        message={
          <p>
            Permanently delete{" "}
            <span className="text-fg-bold">
              {confirmTarget?.model_name_js || "Unnamed Model"}
            </span>
            ? This can&apos;t be undone.
          </p>
        }
        confirmText="Delete forever"
        confirmVariant="destructive"
        isLoading={deleting}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
};

/// A deleted space as one row, the size of a model's, that opens the way a
/// dataset row does; opened, what comes back with it, indented under its name.
const TrashSpace: React.FC<{
  group: TrashGroup;
  open: boolean;
  onToggle: () => void;
  searching: boolean;
  restoring: boolean;
  onRestore: () => void;
  onReviewClosed: () => void;
  children: React.ReactNode;
}> = ({
  group: { header, models },
  open,
  onToggle,
  searching,
  restoring,
  onRestore,
  onReviewClosed,
  children,
}) => {
  const countdown = deletionCountdown(Date.parse(header.purge_at));
  // Opened by its name, a space shows its sections folded; a count opens its
  // own, and a search opens the models it matched.
  const [section, setSection] = useState<string | null>(null);
  const openAt = (label: string | null) => {
    setSection(label);
    if (!open) onToggle();
  };
  const subSpaces = header.sub_spaces.length;
  const tables = models.reduce(
    (n, m) => n + m.tables_scheduled_for_deletion_count,
    0,
  );
  const [reviewing, setReviewing] = useState(false);
  return (
    <div data-testid={`trash-group-${header.space_id}`}>
      <div
        className={`group flex items-center gap-2 item-height px-1 rounded-app select-none cursor-default hover:bg-bg-hover transition-colors ${open ? "sticky top-0 z-10 bg-bg" : ""}`}
      >
        {/* The dot sits in a model's icon column and turns into the chevron on
            hover, as a dataset section's icon does. */}
        <button
          type="button"
          aria-label={
            open ? `Collapse ${header.name}` : `Expand ${header.name}`
          }
          aria-expanded={open}
          onClick={() => (open ? onToggle() : openAt(null))}
          className="group/heading flex min-w-0 items-center gap-2 text-left cursor-pointer"
        >
          <span className="relative flex size-5 shrink-0 items-center justify-center">
            <span className="flex transition-opacity group-hover/heading:opacity-0 group-focus-visible/heading:opacity-0">
              <SpaceDot color={spaceColor(header)} />
            </span>
            <KeyboardArrowDownOutlinedIcon
              className="icon-sm absolute text-fg-muted opacity-0 transition-[opacity,transform] group-hover/heading:opacity-100 group-focus-visible/heading:opacity-100"
              style={{ transform: open ? "none" : "rotate(-90deg)" }}
            />
          </span>
          <span className="truncate text-fg-medium">{header.name}</span>
        </button>
        <span className="flex shrink-0 items-center gap-1 text-fg-muted-regular-small">
          {subSpaces > 0 && (
            <CountLink onClick={() => openAt("Sub-spaces")}>
              {plural(subSpaces, "sub-space")}
            </CountLink>
          )}
          {subSpaces > 0 && models.length > 0 && <span aria-hidden>·</span>}
          {models.length > 0 && (
            <CountLink onClick={() => openAt("Models")}>
              {plural(models.length, "model")}
            </CountLink>
          )}
          {tables > 0 && (
            <>
              <span aria-hidden>·</span>
              <Tooltip
                title={`${plural(tables, "table")} scheduled for deletion — review or transfer`}
                placement="top"
              >
                <span style={{ color: trashRiskColor(models) }}>
                  <CountLink onClick={() => setReviewing(true)}>
                    {plural(tables, "table")}
                  </CountLink>
                </span>
              </Tooltip>
            </>
          )}
          {subSpaces === 0 && models.length === 0 && "Empty"}
        </span>
        <span className="flex-1" />
        <span
          className={`flex w-5 shrink-0 justify-center transition-opacity ${restoring ? "" : "opacity-0 group-hover:opacity-100 focus-within:opacity-100"}`}
        >
          {header.cancellable && (
            <TooltipButton
              title="Restore the space and everything in it"
              aria-label={`Restore ${header.name}`}
              variant="contextual"
              size="xs"
              loading={restoring}
              data-testid={`restore-space-${header.space_id}`}
              onClick={onRestore}
            >
              <RestoreIcon />
            </TooltipButton>
          )}
        </span>
        <Tooltip title={countdown.tooltip} placement="top">
          <span
            className="min-w-6 text-right font-mono text-fg-muted shrink-0 cursor-default"
            style={{
              fontSize: "var(--font-size-x-small)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {countdown.label}
          </span>
        </Tooltip>
      </div>
      <Collapsible open={open} className="pb-2 cursor-default">
        <SpaceSections
          header={header}
          models={models.length}
          section={searching && models.length > 0 ? "Models" : section}
        >
          {children}
        </SpaceSections>
      </Collapsible>
      {reviewing && (
        <DeleteModelDialog
          open
          model={null}
          onClose={() => {
            setReviewing(false);
            onReviewClosed();
          }}
          onDelete={async () => setReviewing(false)}
          isFromTrash
          trashModels={models}
          tableOrigins={
            new Map(
              models.flatMap((m) => {
                const origin = (
                  <OriginPath
                    spaces={[subSpaceOf(m, header) ?? header]}
                    model={m}
                  />
                );
                return m.tables_js.map((t) => [t.table_id_js, origin] as const);
              }),
            )
          }
        />
      )}
    </div>
  );
};

/// What a deleted space holds: the deletion's facts on one quiet line, then its
/// sub-spaces and models as a dataset's sections, one of the two open at a time.
const SpaceSections: React.FC<{
  header: TrashedSpace;
  models: number;
  /// The section a count in the folded row asked for, if any.
  section: string | null;
  children: React.ReactNode;
}> = ({ header, models, section, children }) => {
  const spaceById = useOrgContextStore((s) => s.spaceById);
  const note = header.with_account
    ? "Goes with your account — cancel the account deletion in Settings"
    : header.with_org
      ? `Goes with the organization ${header.with_org} — an owner or admin cancels that from its settings`
      : null;
  return (
    <>
      {/* A section heading's geometry, so icons and text line up with the sections below. */}
      <div className="flex flex-col gap-1 py-2">
        <Cluster
          gap="md"
          className="min-h-(--control-height-sm) pl-8 text-fg-muted-regular"
        >
          {header.deleted_by && (
            <Fact
              icon={<PersonOutlinedIcon className="icon-sm" />}
              label="Deleted by"
            >
              {header.deleted_by}
            </Fact>
          )}
          <Fact
            icon={<CalendarTodayOutlinedIcon className="icon-sm" />}
            label="Deleted on"
          >
            {formatShortDayMonth(header.deletion_scheduled_at)}
          </Fact>
          <Fact
            icon={<DeleteOutlineOutlinedIcon className="icon-sm" />}
            label="Restorable until"
          >
            until {formatShortDayMonth(header.purge_at)}
          </Fact>
        </Cluster>
        {note && (
          <span className="pl-16 text-fg-muted-regular-small">{note}</span>
        )}
      </div>
      <MetaSectionAccordion key={section ?? ""} defaultOpenLabel={section}>
        {header.sub_spaces.length > 0 && (
          <DatasetMetaSection
            icon={<LayersOutlinedIcon className="icon-sm" />}
            label="Sub-spaces"
            meta={<NumberCount count={header.sub_spaces.length} size="sm" />}
            collapsible
            pr="pr-0"
          >
            <ImpactChips
              max={Infinity}
              items={header.sub_spaces.map((sub) => ({
                key: sub.space_id,
                label: (
                  <>
                    <SpaceDot
                      color={spaceColor(
                        spaceById(sub.space_id) ?? {
                          space_id: sub.space_id,
                          color: "",
                        },
                      )}
                    />
                    {sub.name}
                  </>
                ),
              }))}
            />
          </DatasetMetaSection>
        )}
        <DatasetMetaSection
          icon={<DashboardOutlinedIcon className="icon-sm" />}
          label="Models"
          meta={<NumberCount count={models} size="sm" showZero />}
          collapsible
          pr="pr-0"
        >
          {/* No body, so an empty space's "Models 0" has nothing to open. */}
          {models > 0 && children}
        </DatasetMetaSection>
      </MetaSectionAccordion>
    </>
  );
};

const Fact: React.FC<{
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}> = ({ icon, label, children }) => (
  <Tooltip title={label}>
    <span className="inline-flex items-center gap-2 cursor-default">
      <span className="flex size-6 shrink-0 items-center justify-center text-fg-muted">
        {icon}
      </span>
      {children}
    </span>
  </Tooltip>
);

/// A count that opens what it counts, as a trashed model's tables at risk do.
const CountLink: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
}> = ({ onClick, children }) => (
  <span
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      onClick();
    }}
    className="cursor-pointer hover:underline"
  >
    {children}
  </span>
);
